import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, RefreshCw, Check, ArrowLeftRight, Star, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const DISMISSAL_TYPES = [
  { value: 'bowled', label: 'Bowled' },
  { value: 'caught', label: 'Caught' },
  { value: 'lbw', label: 'LBW' },
  { value: 'run_out', label: 'Run Out' },
  { value: 'stumped', label: 'Stumped' },
  { value: 'hit_wicket', label: 'Hit Wkt' },
];

export default function LiveScoring({ user, setUser }) {
  const { matchId } = useParams();
  const navigate = useNavigate();
  
  const [match, setMatch] = useState(null);
  const [innings, setInnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [battingPlayers, setBattingPlayers] = useState([]);
  const [bowlingPlayers, setBowlingPlayers] = useState([]);
  const [thisOver, setThisOver] = useState([]);
  const [batterStats, setBatterStats] = useState({});
  const [bowlerStats, setBowlerStats] = useState([]);
  
  const [activePanel, setActivePanel] = useState(null); // 'wicket', 'wide', 'noball', 'extras'
  const [playerSelectType, setPlayerSelectType] = useState(null);
  const [selectedStriker, setSelectedStriker] = useState('');
  const [selectedNonStriker, setSelectedNonStriker] = useState('');
  const [selectedBowler, setSelectedBowler] = useState('');
  
  // Highlight state
  const [highlightPrompt, setHighlightPrompt] = useState(null); // { eventType, playerName, ballId, overNumber }
  const [showManualHighlight, setShowManualHighlight] = useState(false);

  const fetchMatchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`);
      setMatch(response.data);
      
      const currentInnings = response.data.innings?.find(i => !i.is_completed) || response.data.innings?.[response.data.innings.length - 1];
      setInnings(currentInnings);

      // Fetch balls for this over
      try {
        const ballsRes = await axios.get(`${API}/matches/${matchId}/balls`);
        setThisOver(ballsRes.data?.this_over || []);
        
        // Calculate batter stats from balls
        const stats = {};
        const allBalls = ballsRes.data?.balls || [];
        allBalls.forEach(ball => {
          if (ball.striker_id && !ball.is_extra) {
            if (!stats[ball.striker_id]) {
              stats[ball.striker_id] = { runs: 0, balls: 0, fours: 0, sixes: 0 };
            }
            stats[ball.striker_id].runs += ball.runs || 0;
            stats[ball.striker_id].balls += 1;
            if (ball.runs === 4) stats[ball.striker_id].fours += 1;
            if (ball.runs === 6) stats[ball.striker_id].sixes += 1;
          }
        });
        setBatterStats(stats);
      } catch (e) {
        setThisOver([]);
      }

      // Fetch live stats for bowler details
      try {
        const liveStatsRes = await axios.get(`${API}/matches/${matchId}/live-stats`);
        const currentInningsStats = liveStatsRes.data.innings?.find(i => !i.is_completed) || liveStatsRes.data.innings?.[liveStatsRes.data.innings?.length - 1];
        if (currentInningsStats?.bowlers) {
          setBowlerStats(currentInningsStats.bowlers);
        }
      } catch (e) {
        console.error('Error fetching live stats:', e);
      }

      const battingTeamId = currentInnings?.batting_team_id;
      const bowlingTeamId = currentInnings?.bowling_team_id;
      
      if (battingTeamId) {
        const playersRes = await axios.get(`${API}/players`, { params: { team_id: battingTeamId } });
        setBattingPlayers(playersRes.data);
      }
      
      if (bowlingTeamId) {
        const playersRes = await axios.get(`${API}/players`, { params: { team_id: bowlingTeamId } });
        setBowlingPlayers(playersRes.data);
      }

      if (currentInnings && !currentInnings.striker_id) {
        setPlayerSelectType('batsmen');
      } else if (currentInnings && !currentInnings.current_bowler_id) {
        setPlayerSelectType('bowler');
      }
    } catch (error) {
      console.error('Error fetching match:', error);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMatchData();
  }, [fetchMatchData]);

  // Add highlight to the match
  const addHighlight = async (eventType, playerName, overNumber) => {
    try {
      await axios.post(`${API}/matches/${matchId}/highlights`, {
        event_type: eventType,
        player_name: playerName,
        over_number: overNumber,
        description: getHighlightDescription(eventType, playerName)
      });
      toast.success(`⭐ Highlight added: ${eventType.toUpperCase()}`);
      setHighlightPrompt(null);
    } catch (error) {
      console.error('Failed to add highlight:', error);
      toast.error('Failed to add highlight');
    }
  };

  const getHighlightDescription = (eventType, playerName) => {
    const descriptions = {
      six: `Massive SIX by ${playerName}!`,
      four: `Beautiful FOUR by ${playerName}`,
      wicket: `Wicket! ${playerName} dismissed`,
      catch: `Stunning catch to dismiss ${playerName}`,
      run_out: `Direct hit! ${playerName} run out`,
      stumping: `Quick stumping! ${playerName} out`,
    };
    return descriptions[eventType] || `${eventType} - ${playerName}`;
  };

  // Get striker name for highlights
  const getStrikerName = () => {
    const striker = battingPlayers.find(p => p.player_id === innings?.striker_id);
    return striker?.name || 'Batsman';
  };

  const recordBall = async (runs, isWicket = false, dismissalType = null, isExtra = false, extraType = null, extraRuns = 0) => {
    try {
      const payload = {
        runs: runs,
        is_wicket: isWicket,
        dismissal_type: dismissalType,
        dismissed_player_id: isWicket ? innings?.striker_id : null,
        is_extra: isExtra,
        extra_type: extraType,
        extra_runs: extraRuns
      };

      const response = await axios.post(`${API}/matches/${matchId}/ball`, payload);
      setInnings(response.data.innings);
      setActivePanel(null);

      // Auto-detect highlights
      const currentOver = ((innings?.total_balls || 0) / 6).toFixed(1);
      const strikerName = getStrikerName();

      if (runs === 6 && !isExtra) {
        // Auto-prompt for SIX
        setHighlightPrompt({
          eventType: 'six',
          playerName: strikerName,
          overNumber: parseFloat(currentOver)
        });
      } else if (isWicket) {
        // Auto-prompt for WICKET
        let highlightType = 'wicket';
        if (dismissalType === 'caught') highlightType = 'catch';
        else if (dismissalType === 'run_out') highlightType = 'run_out';
        else if (dismissalType === 'stumped') highlightType = 'stumping';
        
        setHighlightPrompt({
          eventType: highlightType,
          playerName: strikerName,
          overNumber: parseFloat(currentOver)
        });
      }

      if (response.data.is_over_complete) {
        setPlayerSelectType('bowler');
      }

      if (isWicket && innings?.total_wickets < 9) {
        setTimeout(() => {
          setPlayerSelectType('new_batsman');
          setSelectedStriker('');
        }, 300);
      }

      if (response.data.is_innings_complete) {
        navigate(`/scorer`);
      }

      fetchMatchData();
    } catch (error) {
      console.error('Failed to record ball:', error);
    }
  };

  const handleUndo = async () => {
    try {
      const response = await axios.post(`${API}/matches/${matchId}/undo-ball`);
      setInnings(response.data.innings);
      fetchMatchData();
    } catch (error) {
      console.error('Failed to undo:', error);
    }
  };

  const handleSwapBatsmen = async () => {
    try {
      const response = await axios.post(`${API}/matches/${matchId}/swap-batsmen`);
      setInnings(response.data.innings);
      fetchMatchData();
    } catch (error) {
      console.error('Failed to swap:', error);
    }
  };

  const handleSetBatsmen = async () => {
    if (!selectedStriker || !selectedNonStriker || selectedStriker === selectedNonStriker) return;
    try {
      await axios.post(`${API}/matches/${matchId}/set-batsmen`, {
        striker_id: selectedStriker,
        non_striker_id: selectedNonStriker
      });
      setPlayerSelectType(null);
      fetchMatchData();
    } catch (error) {
      console.error('Failed to set batsmen:', error);
    }
  };

  const handleSetNewBatsman = async () => {
    if (!selectedStriker) return;
    try {
      await axios.post(`${API}/matches/${matchId}/set-batsmen`, {
        striker_id: selectedStriker,
        non_striker_id: innings?.non_striker_id
      });
      setPlayerSelectType(null);
      setSelectedStriker('');
      fetchMatchData();
    } catch (error) {
      console.error('Failed to set batsman:', error);
    }
  };

  const handleSetBowler = async () => {
    if (!selectedBowler) return;
    try {
      await axios.post(`${API}/matches/${matchId}/set-bowler`, { bowler_id: selectedBowler });
      setPlayerSelectType(null);
      setSelectedBowler('');
      fetchMatchData();
    } catch (error) {
      console.error('Failed to set bowler:', error);
    }
  };

  const formatOvers = (overs) => {
    const fullOvers = Math.floor(overs || 0);
    const balls = Math.round(((overs || 0) - fullOvers) * 10);
    return `${fullOvers}.${balls}`;
  };

  const getShortName = (playerId, players) => {
    const player = players.find(p => p.player_id === playerId);
    if (!player) return '?';
    const name = player.name.split('(')[0].trim();
    const parts = name.split(' ');
    return parts.slice(-1)[0].slice(0, 10);
  };

  const getBatterStat = (playerId) => {
    return batterStats[playerId] || { runs: 0, balls: 0, fours: 0, sixes: 0 };
  };

  const getStrikeRate = (runs, balls) => {
    if (balls === 0) return '0.00';
    return ((runs / balls) * 100).toFixed(1);
  };

  const getBallDisplay = (ball) => {
    if (!ball) return { label: '-', color: 'bg-gray-200 text-gray-400' };
    if (ball.is_wicket) return { label: 'W', color: 'bg-red-500 text-white' };
    if (ball.is_extra && ball.extra_type === 'wide') return { label: `${ball.extra_runs || 0}wd`, color: 'bg-yellow-500 text-white' };
    if (ball.is_extra && ball.extra_type === 'no_ball') return { label: `${ball.runs || 0}nb`, color: 'bg-[#8B1E3F] text-white' };
    if (ball.is_extra) return { label: `${ball.runs}b`, color: 'bg-amber-400 text-white' };
    if (ball.runs === 6) return { label: '6', color: 'bg-green-500 text-white' };
    if (ball.runs === 4) return { label: '4', color: 'bg-blue-500 text-white' };
    if (ball.runs === 0) return { label: '•', color: 'bg-gray-400 text-white' };
    return { label: String(ball.runs), color: 'bg-blue-400 text-white' };
  };

  const battingTeam = innings?.batting_team_id === match?.team_a_id ? match?.team_a : match?.team_b;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!match || match.status !== 'live') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 p-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800">Match not live</h2>
          <Button onClick={() => navigate('/scorer')} className="mt-4 bg-blue-600">Back</Button>
        </div>
      </div>
    );
  }

  // Player selection screen
  if (playerSelectType) {
    const players = playerSelectType === 'bowler' ? bowlingPlayers : battingPlayers;
    return (
      <div className="min-h-screen bg-stone-100">
        <header className="bg-white border-b px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setPlayerSelectType(null)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-semibold">
            {playerSelectType === 'batsmen' ? 'Select Batsmen' : 
             playerSelectType === 'new_batsman' ? 'New Batsman' : 'Select Bowler'}
          </h1>
        </header>

        <div className="p-4 space-y-4">
          {playerSelectType === 'batsmen' && (
            <>
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Striker *</label>
                <div className="grid grid-cols-2 gap-2">
                  {players.slice(0, 11).map(p => (
                    <button key={p.player_id} onClick={() => setSelectedStriker(p.player_id)}
                      className={`p-3 rounded-lg border text-sm font-medium ${
                        selectedStriker === p.player_id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200'
                      }`}>
                      {selectedStriker === p.player_id && <Check className="w-4 h-4 inline mr-1" />}
                      {getShortName(p.player_id, players)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Non-Striker</label>
                <div className="grid grid-cols-2 gap-2">
                  {players.slice(0, 11).map(p => (
                    <button key={p.player_id} onClick={() => setSelectedNonStriker(p.player_id)}
                      disabled={selectedStriker === p.player_id}
                      className={`p-3 rounded-lg border text-sm font-medium ${
                        selectedNonStriker === p.player_id ? 'bg-[#5D8A8A] text-white border-[#5D8A8A]' 
                        : selectedStriker === p.player_id ? 'bg-gray-100 text-gray-400' : 'bg-white border-gray-200'
                      }`}>
                      {selectedNonStriker === p.player_id && <Check className="w-4 h-4 inline mr-1" />}
                      {getShortName(p.player_id, players)}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleSetBatsmen} className="w-full h-12 bg-blue-600">Confirm</Button>
            </>
          )}

          {playerSelectType === 'new_batsman' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {players.slice(0, 11).map(p => (
                  <button key={p.player_id} onClick={() => setSelectedStriker(p.player_id)}
                    disabled={p.player_id === innings?.non_striker_id}
                    className={`p-3 rounded-lg border text-sm font-medium ${
                      selectedStriker === p.player_id ? 'bg-blue-600 text-white border-blue-600' 
                      : p.player_id === innings?.non_striker_id ? 'bg-gray-100 text-gray-400' : 'bg-white border-gray-200'
                    }`}>
                    {selectedStriker === p.player_id && <Check className="w-4 h-4 inline mr-1" />}
                    {getShortName(p.player_id, players)}
                  </button>
                ))}
              </div>
              <Button onClick={handleSetNewBatsman} className="w-full h-12 bg-blue-600">Confirm</Button>
            </>
          )}

          {playerSelectType === 'bowler' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {players.slice(0, 11).map(p => (
                  <button key={p.player_id} onClick={() => setSelectedBowler(p.player_id)}
                    className={`p-3 rounded-lg border text-sm font-medium ${
                      selectedBowler === p.player_id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200'
                    }`}>
                    {selectedBowler === p.player_id && <Check className="w-4 h-4 inline mr-1" />}
                    {getShortName(p.player_id, players)}
                  </button>
                ))}
              </div>
              <Button onClick={handleSetBowler} className="w-full h-12 bg-blue-600">Confirm</Button>
            </>
          )}
        </div>
      </div>
    );
  }

  const strikerStats = getBatterStat(innings?.striker_id);
  const nonStrikerStats = getBatterStat(innings?.non_striker_id);

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b px-4 py-2 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigate('/scorer')} className="-ml-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <span className="font-semibold text-gray-800">{battingTeam?.short_name || 'BAT'}</span>
          <Badge className="bg-red-100 text-red-600 border-none ml-2 text-xs">LIVE</Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={handleUndo} className="-mr-2">
          <RotateCcw className="w-5 h-5" />
        </Button>
      </header>

      {/* Score Display */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 text-white text-center">
        <div className="text-4xl font-bold">
          {innings?.total_runs || 0}<span className="text-white/70">/{innings?.total_wickets || 0}</span>
        </div>
        <div className="text-white/70 text-sm mt-1">
          {formatOvers(innings?.total_overs)} overs
          {innings?.target && <span className="ml-3">Target: {innings.target}</span>}
        </div>
      </div>

      {/* Batters with Stats */}
      <div className="bg-white mx-3 mt-3 rounded-xl shadow-sm border overflow-hidden">
        {/* Striker */}
        <div className="px-4 py-3 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">*</div>
            <div>
              <div className="font-semibold text-gray-800">{getShortName(innings?.striker_id, battingPlayers)}</div>
              <div className="text-xs text-gray-500">Striker</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <div className="font-bold text-lg text-gray-800">{strikerStats.runs}</div>
              <div className="text-xs text-gray-400">R</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-600">{strikerStats.balls}</div>
              <div className="text-xs text-gray-400">B</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-blue-600">{strikerStats.fours}</div>
              <div className="text-xs text-gray-400">4s</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-green-600">{strikerStats.sixes}</div>
              <div className="text-xs text-gray-400">6s</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-600">{getStrikeRate(strikerStats.runs, strikerStats.balls)}</div>
              <div className="text-xs text-gray-400">SR</div>
            </div>
          </div>
        </div>
        
        {/* Non-Striker */}
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-bold">○</div>
            <div>
              <div className="font-medium text-gray-700">{getShortName(innings?.non_striker_id, battingPlayers)}</div>
              <div className="text-xs text-gray-500">Non-Striker</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <div className="font-bold text-lg text-gray-700">{nonStrikerStats.runs}</div>
              <div className="text-xs text-gray-400">R</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-500">{nonStrikerStats.balls}</div>
              <div className="text-xs text-gray-400">B</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-blue-500">{nonStrikerStats.fours}</div>
              <div className="text-xs text-gray-400">4s</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-green-500">{nonStrikerStats.sixes}</div>
              <div className="text-xs text-gray-400">6s</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-500">{getStrikeRate(nonStrikerStats.runs, nonStrikerStats.balls)}</div>
              <div className="text-xs text-gray-400">SR</div>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="px-4 py-2 bg-stone-50 border-t flex items-center justify-between">
          <button onClick={() => setPlayerSelectType('batsmen')} className="text-xs text-blue-600 font-medium">Change</button>
          <button onClick={handleSwapBatsmen} className="flex items-center gap-1 px-3 py-1.5 bg-[#C5D5D5] text-[#2D5A5A] rounded-full text-xs font-medium">
            <ArrowLeftRight className="w-3 h-3" /> Swap
          </button>
          <button onClick={() => setPlayerSelectType('bowler')} className="text-xs text-blue-600 font-medium">Bowler: {getShortName(innings?.current_bowler_id, bowlingPlayers)}</button>
        </div>
      </div>

      {/* Bowler Stats */}
      {bowlerStats.length > 0 && (
        <div className="mx-3 mt-3 bg-purple-50 rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          <div className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold flex items-center gap-2">
            <span>🎯</span>
            <span>Bowling</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-purple-600 border-b border-purple-100">
                <tr>
                  <th className="text-left py-2 px-3">Bowler</th>
                  <th className="text-center py-2 px-2">O</th>
                  <th className="text-center py-2 px-2">M</th>
                  <th className="text-center py-2 px-2">R</th>
                  <th className="text-center py-2 px-2">W</th>
                  <th className="text-center py-2 px-2">Econ</th>
                </tr>
              </thead>
              <tbody>
                {bowlerStats.slice(0, 4).map((bowler) => (
                  <tr key={bowler.player_id} className={`border-b border-purple-50 ${bowler.is_current ? 'bg-purple-100' : ''}`}>
                    <td className="py-2 px-3 font-medium text-gray-800">
                      {bowler.name?.split(' ').slice(-1)[0]}
                      {bowler.is_current && <span className="text-purple-600"> *</span>}
                    </td>
                    <td className="text-center py-2 px-2">{bowler.overs}</td>
                    <td className="text-center py-2 px-2 text-gray-500">{bowler.maidens || 0}</td>
                    <td className="text-center py-2 px-2">{bowler.runs}</td>
                    <td className="text-center py-2 px-2 font-bold text-purple-600">{bowler.wickets}</td>
                    <td className="text-center py-2 px-2 text-gray-600">{bowler.economy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* This Over */}
      <div className="mx-3 mt-3 bg-white rounded-xl shadow-sm border px-4 py-3 flex items-center justify-center gap-2">
        <span className="text-xs text-gray-500 mr-2">This Over:</span>
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const ball = thisOver[i];
          const { label, color } = getBallDisplay(ball);
          return (
            <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${color}`}>
              {label}
            </div>
          );
        })}
      </div>

      {/* Scoring Panel */}
      <div className="flex-1 mx-3 mt-3 bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Main Runs Grid */}
        <div className="grid grid-cols-4 gap-px bg-gray-200">
          <button onClick={() => recordBall(0)} className="bg-white py-5 text-center active:bg-gray-100">
            <div className="text-2xl font-bold text-gray-800">•</div>
            <div className="text-xs text-gray-500">Dot</div>
          </button>
          <button onClick={() => recordBall(1)} className="bg-white py-5 text-center active:bg-gray-100">
            <div className="text-2xl font-bold text-gray-800">1</div>
          </button>
          <button onClick={() => recordBall(2)} className="bg-white py-5 text-center active:bg-gray-100">
            <div className="text-2xl font-bold text-gray-800">2</div>
          </button>
          <button onClick={() => recordBall(3)} className="bg-white py-5 text-center active:bg-gray-100">
            <div className="text-2xl font-bold text-gray-800">3</div>
          </button>
          
          <button onClick={() => recordBall(4)} className="bg-blue-50 py-5 text-center active:bg-blue-100">
            <div className="text-2xl font-bold text-blue-600">4</div>
          </button>
          <button onClick={() => recordBall(6)} className="bg-green-50 py-5 text-center active:bg-green-100">
            <div className="text-2xl font-bold text-green-600">6</div>
          </button>
          <button onClick={() => setActivePanel(activePanel === 'wide' ? null : 'wide')} 
            className={`py-5 text-center ${activePanel === 'wide' ? 'bg-yellow-200' : 'bg-yellow-50 active:bg-yellow-100'}`}>
            <div className="text-lg font-bold text-yellow-700">Wide</div>
          </button>
          <button onClick={() => setActivePanel(activePanel === 'noball' ? null : 'noball')} 
            className={`py-5 text-center ${activePanel === 'noball' ? 'bg-[#8B1E3F]/20' : 'bg-[#8B1E3F]/10 active:bg-[#8B1E3F]/20'}`}>
            <div className="text-lg font-bold text-[#8B1E3F]">NB</div>
          </button>

          <button onClick={() => setActivePanel(activePanel === 'extras' ? null : 'extras')} 
            className={`py-5 text-center ${activePanel === 'extras' ? 'bg-amber-200' : 'bg-stone-50 active:bg-stone-100'}`}>
            <div className="text-sm font-bold text-gray-700">Bye/LB</div>
          </button>
          <button onClick={() => setActivePanel(activePanel === 'wicket' ? null : 'wicket')} 
            className={`col-span-2 py-5 text-center ${activePanel === 'wicket' ? 'bg-red-600' : 'bg-red-500 active:bg-red-600'}`}>
            <div className="text-lg font-bold text-white">WICKET</div>
          </button>
          <button onClick={handleUndo} className="bg-stone-100 py-5 text-center active:bg-stone-200">
            <RotateCcw className="w-5 h-5 mx-auto text-gray-600" />
            <div className="text-xs text-gray-500">Undo</div>
          </button>
        </div>

        {/* Wide Panel */}
        {activePanel === 'wide' && (
          <div className="p-3 bg-yellow-50 border-t border-yellow-200">
            <p className="text-xs text-yellow-800 mb-2 font-medium">Wide + Runs:</p>
            <div className="grid grid-cols-5 gap-2">
              {[0, 1, 2, 3, 4].map(runs => (
                <button key={runs} onClick={() => recordBall(0, false, null, true, 'wide', runs)}
                  className="bg-yellow-100 p-3 rounded-lg text-yellow-800 font-bold active:bg-yellow-200">
                  +{runs + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No Ball Panel */}
        {activePanel === 'noball' && (
          <div className="p-3 bg-[#8B1E3F]/10 border-t border-[#8B1E3F]/20">
            <p className="text-xs text-[#8B1E3F] mb-2 font-medium">No Ball + Runs:</p>
            <div className="grid grid-cols-5 gap-2">
              {[0, 1, 2, 3, 4, 6].map(runs => (
                <button key={runs} onClick={() => recordBall(runs, false, null, true, 'no_ball', 1)}
                  className="bg-[#8B1E3F]/10 p-3 rounded-lg text-[#8B1E3F] font-bold active:bg-[#8B1E3F]/20">
                  {runs === 0 ? 'NB' : `${runs}+NB`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bye/Leg Bye Panel */}
        {activePanel === 'extras' && (
          <div className="p-3 bg-[#C5D5D5]/30 border-t border-[#5D8A8A]/20">
            <p className="text-xs text-amber-800 mb-2 font-medium">Byes & Leg Byes:</p>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map(runs => (
                <button key={`bye-${runs}`} onClick={() => recordBall(runs, false, null, true, 'bye', 0)}
                  className="bg-amber-100 p-2 rounded-lg text-amber-800 font-bold text-sm active:bg-amber-200">
                  {runs}B
                </button>
              ))}
              {[1, 2, 3, 4].map(runs => (
                <button key={`lb-${runs}`} onClick={() => recordBall(runs, false, null, true, 'leg_bye', 0)}
                  className="bg-amber-100 p-2 rounded-lg text-amber-800 font-bold text-sm active:bg-amber-200">
                  {runs}LB
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Wicket Panel */}
        {activePanel === 'wicket' && (
          <div className="p-3 bg-red-50 border-t border-red-200">
            <p className="text-xs text-red-800 mb-2 font-medium">Dismissal Type:</p>
            <div className="grid grid-cols-3 gap-2">
              {DISMISSAL_TYPES.map(d => (
                <button key={d.value} onClick={() => recordBall(0, true, d.value)}
                  className="bg-red-100 p-3 rounded-lg text-red-800 font-medium active:bg-red-200">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Manual Highlight Button */}
        <div className="p-3 border-t">
          <button
            onClick={() => setShowManualHighlight(true)}
            className="w-full flex items-center justify-center gap-2 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg text-amber-700 font-medium active:from-amber-100 active:to-yellow-100"
            data-testid="manual-highlight-btn"
          >
            <Star className="w-4 h-4" />
            Mark Highlight
          </button>
        </div>
      </div>

      {/* Auto Highlight Prompt */}
      {highlightPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-4 text-white text-center">
              <Sparkles className="w-8 h-8 mx-auto mb-2" />
              <h3 className="text-lg font-bold">
                {highlightPrompt.eventType === 'six' && '🔥 MAXIMUM!'}
                {highlightPrompt.eventType === 'wicket' && '🎯 WICKET!'}
                {highlightPrompt.eventType === 'catch' && '🙌 CAUGHT!'}
                {highlightPrompt.eventType === 'run_out' && '⚡ RUN OUT!'}
                {highlightPrompt.eventType === 'stumping' && '🧤 STUMPED!'}
              </h3>
              <p className="text-white/90 text-sm mt-1">Add to Match Highlights?</p>
            </div>
            <div className="p-4">
              <div className="text-center mb-4">
                <div className="text-gray-800 font-semibold">{highlightPrompt.playerName}</div>
                <div className="text-gray-500 text-sm">Over {highlightPrompt.overNumber}</div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setHighlightPrompt(null)}
                  className="flex-1 py-3 px-4 bg-gray-100 rounded-xl text-gray-600 font-medium"
                >
                  Skip
                </button>
                <button
                  onClick={() => addHighlight(highlightPrompt.eventType, highlightPrompt.playerName, highlightPrompt.overNumber)}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl text-white font-medium flex items-center justify-center gap-2"
                  data-testid="confirm-highlight-btn"
                >
                  <Star className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Highlight Modal */}
      {showManualHighlight && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Star className="w-5 h-5" />
                Add Highlight
              </h3>
              <button onClick={() => setShowManualHighlight(false)} className="p-1 hover:bg-white/20 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-500 text-sm mb-4">Select highlight type:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { type: 'six', label: '6️⃣ Six', color: 'bg-green-100 text-green-700' },
                  { type: 'four', label: '4️⃣ Four', color: 'bg-blue-100 text-blue-700' },
                  { type: 'wicket', label: '🎯 Wicket', color: 'bg-red-100 text-red-700' },
                  { type: 'catch', label: '🙌 Great Catch', color: 'bg-purple-100 text-purple-700' },
                  { type: 'run_out', label: '⚡ Run Out', color: 'bg-orange-100 text-orange-700' },
                  { type: 'other', label: '⭐ Other', color: 'bg-amber-100 text-amber-700' },
                ].map(h => (
                  <button
                    key={h.type}
                    onClick={() => {
                      addHighlight(h.type, getStrikerName(), ((innings?.total_balls || 0) / 6).toFixed(1));
                      setShowManualHighlight(false);
                    }}
                    className={`p-3 rounded-xl font-medium ${h.color} active:opacity-80`}
                    data-testid={`highlight-${h.type}`}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom spacing */}
      <div className="h-4"></div>
    </div>
  );
}
